import { fetch_request } from '../../../service/request';
import {
  applyMessengerV2Snapshot,
  ensureMessengerV2ThreadReadyState,
  fetchMessengerV2ConversationSnapshot,
  resolveMessengerV2Manager,
} from './messengerV2MlsGateway.js';
import { isMessengerV2SendCapableGroup, normalizeMessengerV2ReadyInfo } from './groupReadiness.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const createHttpError = (response, fallbackMessage = null) => {
  const error = new Error(fallbackMessage || `HTTP ${response.status}`);
  error.status = response.status;
  error.response = response;
  return error;
};

const toNumericSuiteOrDefault = (value, fallback = DEFAULT_MLS_CIPHERSUITE) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const patchConversationWithSnapshot = (conversation, snapshot) => {
  if (!conversation || typeof conversation !== 'object') return conversation;
  if (!snapshot || typeof snapshot !== 'object') return conversation;

  const groupId = toStringOrNull(snapshot.group_id ?? snapshot.groupId ?? null);
  if (!groupId) return conversation;

  const ciphersuite = toNumericSuiteOrDefault(
    snapshot.ciphersuite ??
      conversation?.mls_ciphersuite ??
      conversation?.meta?.mls_ciphersuite ??
      null,
  );

  return {
    ...conversation,
    meta: {
      ...(conversation.meta || {}),
      mls_group_id: groupId,
      e2ee_protocol: 'mls',
      e2ee_enabled: true,
      mls_ciphersuite: ciphersuite,
      ciphersuite,
      mls_want_pq: true,
    },
    mls_group_id: groupId,
    e2ee_protocol: 'mls',
    e2ee_enabled: true,
    mls_ciphersuite: ciphersuite,
    ciphersuite,
    mls_want_pq: true,
  };
};

export const fetchMessengerV2Conversation = async ({ conversationId } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  const response = await fetch_request(`/api/conversations/${normalizedConversationId}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });
  if (!response.ok) {
    throw createHttpError(response);
  }

  const json = await response.json();
  if (!json || typeof json !== 'object') {
    return null;
  }

  return {
    ...json,
    id: toStringOrNull(json.id ?? normalizedConversationId) || normalizedConversationId,
  };
};

export const loadMessengerV2ActiveThread = async ({
  manager = null,
  conversationId,
  currentUserId,
  currentUserName = null,
  clientId = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  if (!normalizedCurrentUserId) {
    throw new Error('currentUserId is required');
  }

  const conversation = await fetchMessengerV2Conversation({
    conversationId: normalizedConversationId,
  });

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: toNumericSuiteOrDefault(
        conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? null,
      ),
      wantPQ: true,
      serverManaged: true,
    });

  const canonicalIdentity =
    (await Promise.resolve(
      resolvedManager?.ensureIdentity?.({
        name: identityName,
        ciphersuite: toNumericSuiteOrDefault(
          conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? null,
        ),
      }),
    ).catch(() => null)) || null;
  const snapshotClientId =
    toStringOrNull(
      canonicalIdentity?.clientId ??
        canonicalIdentity?.client_id ??
        resolvedManager?.identityClientId ??
        clientId,
    ) || null;

  const snapshot = await fetchMessengerV2ConversationSnapshot({
    conversationId: normalizedConversationId,
    clientId: snapshotClientId,
  }).catch(() => null);

  const conversationWithSnapshot = patchConversationWithSnapshot(conversation, snapshot);
  const groupId = toStringOrNull(
    conversationWithSnapshot?.mls_group_id ?? conversationWithSnapshot?.meta?.mls_group_id ?? null,
  );

  return {
    manager: resolvedManager,
    identityName,
    conversation: conversationWithSnapshot,
    snapshot,
    groupId,
    ready: false,
    readyInfo: null,
  };
};

export const applyMessengerV2ActiveThreadSnapshot = async ({
  manager = null,
  snapshot = null,
  conversation = null,
  conversationId = null,
  currentUserId,
  currentUserName = null,
} = {}) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  const normalizedConversationId = toStringOrNull(conversationId ?? conversation?.id ?? null);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedConversationId || !normalizedCurrentUserId) {
    return null;
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: toNumericSuiteOrDefault(
        conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? null,
      ),
      wantPQ: true,
      serverManaged: true,
    });

  await Promise.resolve(
    applyMessengerV2Snapshot({
      manager: resolvedManager,
      snapshot,
      source: 'messages_v2_active_thread',
      conversationId: normalizedConversationId,
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: toNumericSuiteOrDefault(
        conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? null,
      ),
    }),
  ).catch(() => null);

  return resolvedManager;
};

export const convergeMessengerV2ActiveThreadReady = async ({
  manager = null,
  conversation = null,
  conversationId = null,
  groupId = null,
  currentUserId,
  currentUserName = null,
  allowRecovery = false,
  allowPendingWelcomeJoin = false,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId ?? conversation?.id ?? null);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  const normalizedGroupId = toStringOrNull(
    groupId ?? conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null,
  );
  if (!normalizedConversationId || !normalizedCurrentUserId || !normalizedGroupId) {
    return null;
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: toNumericSuiteOrDefault(
        conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? null,
      ),
      wantPQ: true,
      serverManaged: true,
    });

  const readySync = await Promise.resolve(
    ensureMessengerV2ThreadReadyState({
      manager: resolvedManager,
      conversation,
      conversationId: normalizedConversationId,
      groupId: normalizedGroupId,
      currentUserId: normalizedCurrentUserId,
      currentUserName: identityName,
      waitForReady: false,
      reason: 'messages_v2_active_thread',
      allowRecovery,
      allowPendingWelcomeJoin,
    }),
  ).catch(() => null);

  let readyInfo =
    readySync?.readyInfo ??
    readySync?.group ??
    readySync?.ensuredGroup ??
    readySync?.convergedGroup ??
    null;
  const ready = isMessengerV2SendCapableGroup(readyInfo, {
    clientId: readySync?.senderClientId ?? null,
  });
  const normalizedReadyInfo = normalizeMessengerV2ReadyInfo(readyInfo, ready);

  return {
    readySync,
    readyInfo: normalizedReadyInfo,
    groupId: toStringOrNull(normalizedReadyInfo?.groupId ?? normalizedGroupId ?? null),
    conversation: normalizedReadyInfo
      ? patchConversationWithSnapshot(conversation, normalizedReadyInfo)
      : conversation,
    ready,
  };
};

const synchronizeMessengerV2ActiveThreadOwned = async ({
  manager = null,
  conversationId = null,
  currentUserId,
  currentUserName = null,
  clientId = null,
  allowRecovery = false,
  allowPendingWelcomeJoin = false,
  onSeeded = null,
} = {}) => {
  const seeded = await loadMessengerV2ActiveThread({
    manager,
    conversationId,
    currentUserId,
    currentUserName,
    clientId,
  });
  const resolvedManager = seeded?.manager ?? manager ?? null;
  const latestSnapshot = seeded?.snapshot ?? null;
  const latestConversation = seeded?.conversation ?? null;
  const latestGroupId = seeded?.groupId ?? null;

  await applyMessengerV2ActiveThreadSnapshot({
    manager: resolvedManager,
    snapshot: latestSnapshot,
    conversation: latestConversation,
    conversationId,
    currentUserId,
    currentUserName,
  });

  if (typeof onSeeded === 'function') {
    await onSeeded({
      ...seeded,
      manager: resolvedManager,
      conversation: latestConversation,
      snapshot: latestSnapshot,
      groupId: latestGroupId,
    });
  }

  const readyResult = await convergeMessengerV2ActiveThreadReady({
    manager: resolvedManager,
    conversation: latestConversation,
    conversationId,
    groupId: latestGroupId ?? null,
    currentUserId,
    currentUserName,
    allowRecovery,
    allowPendingWelcomeJoin,
  });

  return {
    ...seeded,
    ready: readyResult?.ready === true,
    readyInfo: readyResult?.readyInfo ?? seeded?.readyInfo ?? null,
    conversation: readyResult?.conversation ?? latestConversation ?? null,
    snapshot: latestSnapshot,
    readyResult: readyResult ?? null,
  };
};

export const synchronizeMessengerV2ActiveThread = async ({
  manager = null,
  conversationId = null,
  currentUserId,
  currentUserName = null,
  clientId = null,
  allowRecovery = false,
  allowPendingWelcomeJoin = false,
  onSeeded = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  if (!normalizedCurrentUserId) {
    throw new Error('currentUserId is required');
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
      wantPQ: true,
      serverManaged: true,
    });
  const task = () =>
    synchronizeMessengerV2ActiveThreadOwned({
      manager: resolvedManager,
      conversationId: normalizedConversationId,
      currentUserId: normalizedCurrentUserId,
      currentUserName: identityName,
      clientId,
      allowRecovery,
      allowPendingWelcomeJoin,
      onSeeded,
    });

  if (typeof resolvedManager?.runActiveThreadSync === 'function') {
    return resolvedManager.runActiveThreadSync({
      conversationId: normalizedConversationId,
      task,
    });
  }
  return task();
};

export const recoverMessengerV2ConversationCatalog = async ({
  manager = null,
  conversations = [],
  currentUserId,
  currentUserName = null,
  clientId = null,
  shouldContinue = null,
  onResult = null,
} = {}) => {
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedCurrentUserId) {
    throw new Error('currentUserId is required');
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
      wantPQ: true,
      serverManaged: true,
    });
  if (!resolvedManager || typeof resolvedManager.convergeGroupState !== 'function') {
    throw new Error('V2 MLS manager unavailable for conversation recovery');
  }

  const uniqueConversations = [];
  const seenConversationIds = new Set();
  (Array.isArray(conversations) ? conversations : []).forEach((conversation) => {
    const conversationId = toStringOrNull(conversation?.id ?? null);
    if (!conversationId || seenConversationIds.has(conversationId)) return;
    seenConversationIds.add(conversationId);
    uniqueConversations.push(conversation);
  });

  const results = [];
  for (const conversation of uniqueConversations) {
    if (typeof shouldContinue === 'function' && shouldContinue() === false) break;

    const conversationId = toStringOrNull(conversation?.id ?? null);
    const groupId = toStringOrNull(
      conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null,
    );
    let result;
    try {
      const readyInfo =
        (await resolvedManager.convergeGroupState({
          conversationId,
          groupId,
          preferServerSnapshot: true,
          allowRecovery: true,
          allowPendingWelcomeJoin: true,
        })) ?? null;
      const resolvedGroupId = toStringOrNull(
        readyInfo?.groupId ?? readyInfo?.group_id ?? groupId ?? null,
      );
      const ready = isMessengerV2SendCapableGroup(readyInfo, { clientId });
      result = {
        conversation,
        conversationId,
        groupId: resolvedGroupId,
        ready,
        readyInfo: normalizeMessengerV2ReadyInfo(readyInfo, ready),
        error: null,
      };
    } catch (error) {
      result = {
        conversation,
        conversationId,
        groupId,
        ready: false,
        readyInfo: null,
        error,
      };
    }
    results.push(result);
    if (typeof onResult === 'function') {
      await onResult(result);
    }
  }

  return results;
};

const activeThreadService = {
  applyMessengerV2ActiveThreadSnapshot,
  convergeMessengerV2ActiveThreadReady,
  fetchMessengerV2Conversation,
  loadMessengerV2ActiveThread,
  recoverMessengerV2ConversationCatalog,
  synchronizeMessengerV2ActiveThread,
};

export default activeThreadService;
